<template>
  <div id="aoermabank" class="aoe-payment-gateway">
    <h5>{{ t('rma.BankPaymentHeader') }}</h5>

    <div class="row mt-3">
      <div class="col-12 bank-payment-entry">
        {{ t('rma.BankPaymentInstruction') }}
      </div>
    </div>

    <div v-if="loading" class="row mt-3">
      <div class="col-12 text-center">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">{{ t('labels.Loading') }}</span>
        </div>
      </div>
    </div>

    <div v-else-if="bankDetails" class="row mt-3">
      <div class="col-md-6">
        <div class="bank-details-container">
          <div class="bank-details-header">{{ t('rma.BankPaymentYourBankDetails') }}</div>
          <div class="bank-details-item">
            <div class="bank-details-label">{{ t('rma.BankPaymentAccountHolder') }}:</div>
            <div class="bank-details-value">{{ bankDetails.accountHolder }}</div>
          </div>
          <div class="bank-details-item">
            <div class="bank-details-label">{{ t('rma.BankPaymentAccountNumber') }}:</div>
            <div class="bank-details-value">{{ maskAccountNumber(bankDetails.accountNumber) }}</div>
          </div>
          <div class="bank-details-item">
            <div class="bank-details-label">{{ t('rma.BankPaymentRoutingNumber') }}:</div>
            <div class="bank-details-value">{{ bankDetails.abaNumber }}</div>
          </div>
        </div>
      </div>
      <div class="col-md-6">
        <div class="payment-amount-container">
          <div class="payment-amount-header">{{ t('rma.BankPaymentAmount') }}</div>
          <div class="payment-amount-value">{{ chargeDisplay }}</div>
        </div>
      </div>
    </div>

    <div v-else class="row mt-3">
      <div class="col-12 bank-payment-error">
        {{ t('rma.BankPaymentNoBankDetailsFound') }}
      </div>
    </div>

    <div v-if="bankDetails" class="row mt-3">
      <div class="col-12 text-center bank-payment-submit">
        <KButton
          @click="createBankPaymentHandler($event)"
          class="baseBtn primaryBtn payment-bank-create-button"
          :rounded="'large'"
          :theme-color="'checkoutContinue'"
          :disabled="loading || processingPayment">
          {{ processingPayment ? t('rma.BankPaymentProcessing') : t('rma.BankPaymentPayWithBank') }}
        </KButton>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'
const { t } = useI18n()
import { ref, watch } from 'vue'
import { onMounted } from 'vue'
import { useLogger } from 'vue-logger-plugin'
import { Button as KButton } from '@progress/kendo-vue-buttons'
import { BeeVirtualCheckGetListConnector } from '@/api/VirtualCheck.ts'
import { GetBeeNumberAuthenicatedUser } from '@/stores/SettingsStore'
import { SetIsLoading } from '@/services/AoeHelpers'
import { RmaBankPaymentCreate } from '@/api/RMA.ts'
import { FormatCurrency } from '@/services/currencyFormatter'
import type { IBeeVirtualCheckItem } from '@/types/VirtualCheckTypes.d.ts'

const props = defineProps<{
  rmaNumber: string
  charge: number
  systemKeyword: string
  name: string
}>()

const emit = defineEmits(['update-cart'])

const logger = useLogger()
logger.debug('BankPaymentRmaEntry props', {
  name: props.name,
  systemKeyword: props.systemKeyword,
  rmaNumber: props.rmaNumber,
  charge: props.charge,
})

const loading = ref(true)
const processingPayment = ref(false)
const bankDetails = ref<IBeeVirtualCheckItem | null>(null)
const charge = ref<number>(props.charge ?? 0)
const chargeDisplay = ref<string>(FormatCurrency(charge.value, null) || charge.value.toFixed(2))

watch(() => props.charge, (newCharge) => {
  if (newCharge !== undefined) {
    charge.value = newCharge
    chargeDisplay.value = FormatCurrency(newCharge, null) || newCharge.toFixed(2)
  }
}, { immediate: true })

// Mask account number for security (show only last 4 digits)
function maskAccountNumber(accountNumber: string): string {
  if (!accountNumber) return ''
  if (accountNumber.length <= 4) return accountNumber

  const lastFour = accountNumber.slice(-4)
  return '••••••' + lastFour
}

onMounted(async () => {
  logger.debug('onMounted BankPaymentRmaEntry')

  if (!props.rmaNumber) {
    logger.error('props.rmaNumber is missing')
    loading.value = false
    return
  }

  try {
    // Get authenticated user's BEE number
    const beeNumber = await GetBeeNumberAuthenicatedUser()

    if (!beeNumber) {
      logger.error('No BEE number found for authenticated user')
      loading.value = false
      return
    }

    // Get bank details using PwsBeeBankingGetList API
    const response = await BeeVirtualCheckGetListConnector()

    if (response && response.items && response.items.length > 0) {
      // Find the bank account with codeIdBankingType.codeId = "PAYMENT"
      const paymentBankAccount = response.items[0]

      if (paymentBankAccount) {
        bankDetails.value = paymentBankAccount
        logger.debug('Payment bank details retrieved:', bankDetails.value)
      } else {
        // Don't fall back to the first bank account, display NoBankDetailsFound message instead
        logger.warn('No payment-specific bank account found, showing NoBankDetailsFound message')
        bankDetails.value = null
      }
    } else {
      logger.warn('No bank details found for BEE number:', beeNumber)
    }
  } catch (error) {
    logger.error('Error retrieving bank details:', error)
  } finally {
    loading.value = false
  }
})

async function createBankPaymentHandler(event: MouseEvent) {
  logger.debug('createBankPaymentHandler', event)

  if (!props.rmaNumber || !bankDetails.value) {
    logger.error('RMA number or bank details are missing')
    return
  }

  try {
    processingPayment.value = true
    SetIsLoading(true)

    await RmaBankPaymentCreate({
      rmaNumber: props.rmaNumber,
      systemKeyword: props.systemKeyword,
      charge: charge.value,
      opaPaymentAttributes: {
        accountHolder: bankDetails.value.accountHolder,
        accountNumber: bankDetails.value.accountNumber,
        abaNumber: bankDetails.value.abaNumber,
      },
    })

    // Notify parent component that payment was created
    emit('update-cart', event)

    // Send a message to notify other components about the payment creation - this is what
    // AOERMAEntry.vue's rmaPaymentMessageHandler listens for to refresh the RMA data
    window.postMessage(
      {
        messageType: 'aoepaymentcreated',
        messageData: { success: true },
      },
      window.location.origin,
    )

    logger.debug('RMA bank payment created successfully')
  } catch (error) {
    logger.error('Error creating RMA bank payment:', error)

    // Send error message
    window.postMessage(
      {
        messageType: 'aoepaymentcreationerror',
        messageData: error,
      },
      window.location.origin,
    )
  } finally {
    processingPayment.value = false
    SetIsLoading(false)
  }
}
</script>

<style lang="scss" scoped>
.bank-details-container {
  background-color: var(--vt-c-white-soft);
  border-radius: $radius-md;
  padding: $spacing-4;
  margin-bottom: 15px;
}

.bank-details-header {
  @include typography-body-large;
  @include font-weight-bold;
  margin-bottom: 10px;
  color: #1a6e7d;
}

.bank-details-item {
  display: flex;
  margin-bottom: 8px;
}

.bank-details-label {
  @include font-weight-medium;
  width: 40%;
}

.bank-details-value {
  width: 60%;
}

.payment-amount-container {
  background-color: #e0f2f5;
  border-radius: $radius-md;
  padding: $spacing-4;
  text-align: center;
}

.payment-amount-header {
  @include typography-body-large;
  @include font-weight-bold;
  margin-bottom: 10px;
  color: #1a6e7d;
}

.payment-amount-value {
  @include typography-h3;
  @include font-weight-bold;
  color: #1a6e7d;
}

.bank-payment-error {
  color: #dc3545;
  text-align: center;
  padding: $spacing-2-5;
  background-color: #f8d7da;
  border-radius: $radius-sm;
}
</style>
